import { Button, Dropdown, Empty, Modal, Tag, Tooltip, Typography } from 'antd';

import CustomResponsiveTable from '../CustomResponsiveTable';
import {
	Material,
	MaterialLength,
	MaterialReservation,
} from '../../interfaces/MaterialInventory.interface';
import { CheckCircleFilled, MinusCircleOutlined, WarningFilled } from '@ant-design/icons';
import { useLazyGetMaterialByIdQuery } from '../../redux/api/material';
import { type HTMLAttributes, ReactNode, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { showAddMaterialModal } from '../../redux/slices/material';
import ReturnRemnantsModal from '../ReturnRemnantModal';
import MaterialReservationModal from '../MaterialReservationModal';
import {
	ANGLE,
	CHANNEL,
	HEX_BAR,
	MATERIAL_STATUSES,
	PLATE,
	RECTANGLE_BAR,
	RECTANGLE_TUBE,
	ROUND_BAR,
	ROUND_TUBE,
} from '../../config/constants';
import { Lineitem } from '../../interfaces/Ordel.Interface';
import MaterialInventoryDetails from '../MaterialInventory/MaterialInventoryDetails.tsx';
import ExpandableTextCell from './ExpandableTextCellProps.tsx';
import {
	BundleIdFilterDropdown,
	TypeFilterDropdown,
	ShelfFilterDropdown,
	RangeFilterDropdown,
	CertificateFilterDropdown,
	MinimumFilterDropdown,
} from '../MaterialFilters/MaterialFilters.tsx';
import { NavigateFunction } from 'react-router';
import { useHasMultipleBuildings } from '../../hooks/useHasMultipleBuildings';
import { useLocationLabel } from '../../utils/locationLabel';

const { Text } = Typography;

interface Props {
	data: Material[];
	isLoading: boolean;
	recordsTotal?: number;
	currentPage?: number;
	onChange?: () => void;
	onChangePage?: (page: number) => void;
	selectedShape: string | undefined;
	setSelectedMaterial?: (record: Material) => void;
	setShowMaterialModal?: (value: boolean) => void;
	refetchData?: () => void;
	tableProps: {
		size: 'small' | 'middle' | 'large';
		expandable?: boolean;
		scrollY?: string;
		empty?: ReactNode;
	};
	showActionsColumn: boolean;
	showTotalLengthColumn?: boolean;
	showStatusColum?: boolean;
	showDetailsPane?: boolean;
	showMaterialCertificatesPane?: boolean;
	showReturnRemnantButton?: boolean;
	selectedPreviewMaterial?: string | null;
	onChangePreviewMaterial?: (id: string) => void;
	lineItem?: Lineitem;
	material_cut_length?: string | null;
	saw_kerf?: number;
	material_cut_quantity?: number;
	total_required_length?: number;
	required_trim_cut_length?: number;
	queryParams?: URLSearchParams;
	navigate?: NavigateFunction;
	types?: any;
	// shelves?: any;
	locations?: any[];
	onSearchLocation?: (value: string) => void;
	locationsLoading?: boolean;
	categories?: any;
	heightRange?: { min: number; max: number };
	widthRange?: { min: number; max: number };
	outerDiameterRange?: { min: number; max: number };
	innerDiameterRange?: { min: number; max: number };
	wallThicknessRange?: { min: number; max: number };
	totalLengthRange?: { min: number; max: number };
	totalAvailableLengthRange?: { min: number; max: number };
	materialLengthsCountRange?: { min: number; max: number };
	lockedBuildingId?: string | null;
}

const MaterialInventoryTable = (props: Props) => {
	const dispatch = useDispatch();
	const [{ confirm }, contextHolder] = Modal.useModal();
	const showBuildingColumn = useHasMultipleBuildings();
	const formatLocation = useLocationLabel();

	const [getMaterial] = useLazyGetMaterialByIdQuery();

	const [selectedMaterialDocument, setSelectedMaterialDocument] = useState(
		'Material Certificate'
	);

	const [reserveMultipleLengths, setReserveMultipleLengths] =
		useState<boolean>(false);

	const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);
	const [selectedMaterial, setSelectedMaterial] = useState<Material | null>(
		null
	);
	const [selectedMaterialLength, setSelectedMaterialLength] =
		useState<MaterialLength | null>(null);
	const [selectedMaterialReservation, setSelectedMaterialReservation] =
		useState<MaterialReservation | null>(null);

	const [isMaterialReservationModalOpen, setIsMaterialReservationModalOpen] =
		useState<boolean>(false);

	const [isReturnRemnantsModalOpen, setIsReturnRemnantsModalOpen] =
		useState<boolean>(false);

	const [forceRefetch, setForceRefetch] = useState<boolean>(false);

	const filtersEnabled = props.queryParams && props.navigate;

	const currentPageFromUrl = Number(props.queryParams?.get('page')) || 1;

	const tableColumns: any = [
		{
			title: 'Bundle | Length ID',
			render: (record: Material) => {
				const materialLengths = record?.material_lengths;

				if (!Array.isArray(materialLengths) || materialLengths.length === 0) {
					return 'N/A';
				}

				const allIdsText = materialLengths
					.map((item) => item.bundle_friendly_id?.trim())
					.filter(Boolean)
					.sort()
					.join(', ');

				return <ExpandableTextCell text={allIdsText} />;
			},
			visible: true,
			// sorter: true,
			key: 'first_bundle_friendly_id',

			...(filtersEnabled
				? BundleIdFilterDropdown({
						queryParams: props.queryParams!,
						navigate: props.navigate!,
				  })
				: {}),
		},
		{
			title: 'Type',
			render: (record: Material) => `${record?.type?.display_name}`,
			visible: true,
			sorter: true,
			key: 'type',
			...(filtersEnabled && props.types && props.categories
				? TypeFilterDropdown(
						{ queryParams: props.queryParams!, navigate: props.navigate! },
						props.types,
						props.categories
				  )
				: {}),
		},
		{
			title: 'Building',
			render: (record: Material) => {
				const materialLengths = record?.material_lengths;
				if (!Array.isArray(materialLengths) || materialLengths.length === 0) {
					return <Typography.Text type="secondary">—</Typography.Text>;
				}
				const buildingNames = materialLengths
					.map((item) => item.building_name)
					.filter(Boolean);
				const uniqueBuildings = [...new Set(buildingNames)];
				return uniqueBuildings.join(', ') || <Typography.Text type="secondary">—</Typography.Text>;
			},
			visible: showBuildingColumn,
			key: 'building',
		},
		{
			title: 'Location',
			render: (record: Material) => {
				const materialLengths = record?.material_lengths;

				if (!Array.isArray(materialLengths) || materialLengths.length === 0) {
					return (
						<Typography.Text type={'secondary'}>
							No Location Assigned
						</Typography.Text>
					);
				}

				const locationNames = materialLengths
					.map((item) => formatLocation(item.display_name))
					.filter(Boolean);

				const uniqueLocations = [...new Set(locationNames)];

				const allLocationsText = uniqueLocations.join(', ');

				if (!allLocationsText) {
					return (
						<Typography.Text type={'secondary'}>
							No Location Assigned
						</Typography.Text>
					);
				}

				return <ExpandableTextCell text={allLocationsText} />;
			},
			visible: true,
			// sorter: true,
			key: 'unique_locations_combined',
			...(filtersEnabled && props.locations
				? ShelfFilterDropdown(
						{ queryParams: props.queryParams!, navigate: props.navigate! },
						props.locations,
						props.onSearchLocation,
						props.locationsLoading,
						formatLocation
				  )
				: {}),
		},
		{
			title: 'Height',
			render: (record: Material) => Number(record?.height),
			visible:
				props.selectedShape === RECTANGLE_BAR ||
				props.selectedShape === RECTANGLE_TUBE ||
				props.selectedShape === HEX_BAR ||
				props.selectedShape === PLATE ||
				props.selectedShape === ANGLE ||
				props.selectedShape === CHANNEL,
			key: 'height',
			sorter: true,

			...(filtersEnabled
				? RangeFilterDropdown(
						{ queryParams: props.queryParams!, navigate: props.navigate! },
						'height',
						'Height',
						props.heightRange
				  )
				: {}),
		},
		{
			title: 'Width',
			render: (record: Material) => Number(record?.width),
			visible:
				props.selectedShape === RECTANGLE_BAR ||
				props.selectedShape === RECTANGLE_TUBE ||
				props.selectedShape === PLATE ||
				props.selectedShape === ANGLE ||
				props.selectedShape === CHANNEL,
			key: 'width',
			sorter: true,

			...(filtersEnabled
				? RangeFilterDropdown(
						{ queryParams: props.queryParams!, navigate: props.navigate! },
						'width',
						'Width',
						props.widthRange
				  )
				: {}),
		},
		{
			title: 'Outer Diameter',
			render: (record: Material) => Number(record?.outer_diameter),
			visible:
				props.selectedShape === ROUND_BAR || props.selectedShape === ROUND_TUBE,
			key: 'outer_diameter',
			sorter: true,

			...(filtersEnabled
				? RangeFilterDropdown(
						{ queryParams: props.queryParams!, navigate: props.navigate! },
						'outer_diameter',
						'Outer Diameter',
						props.outerDiameterRange
				  )
				: {}),
		},
		{
			title: 'Inner Diameter',
			render: (record: Material) => Number(record?.inner_diameter),
			visible: props.selectedShape === ROUND_TUBE,
			key: 'inner_diameter',
			sorter: true,

			...(filtersEnabled
				? RangeFilterDropdown(
						{ queryParams: props.queryParams!, navigate: props.navigate! },
						'inner_diameter',
						'Inner Diameter',
						props.innerDiameterRange
				  )
				: {}),
		},
		{
			title: 'Wall Thickness',
			render: (record: Material) => Number(record?.wall_thickness),
			visible:
				props.selectedShape === RECTANGLE_TUBE ||
				props.selectedShape === ANGLE ||
				props.selectedShape === CHANNEL,
			key: 'wall_thickness',
			sorter: true,

			...(filtersEnabled
				? RangeFilterDropdown(
						{ queryParams: props.queryParams!, navigate: props.navigate! },
						'wall_thickness',
						'Wall Thickness',
						props.wallThicknessRange
				  )
				: {}),
		},
		{
			title: 'Lengths',
			width: '10%',
			visible: true,
			dataIndex: 'material_lengths_count',
			key: 'material_lengths_count',
			sorter: true,

			...(filtersEnabled
				? RangeFilterDropdown(
						{ queryParams: props.queryParams!, navigate: props.navigate! },
						'material_lengths_count',
						'Lengths',
						props.materialLengthsCountRange
				  )
				: {}),
		},
		{
			title: 'Total Length',
			render: (record: Material) => `${record.total_length || 0} IN.`,
			visible: props.showTotalLengthColumn,
			sorter: true,
			key: 'total_length',

			...(filtersEnabled
				? RangeFilterDropdown(
						{ queryParams: props.queryParams!, navigate: props.navigate! },
						'total_length',
						'Total Length',
						props.totalLengthRange
				  )
				: {}),
		},
		{
			title: 'Total Available Length',
			render: (record: Material) => {
				const availableLength = record.total_available_length || 0;
				const requiredLength = props.total_required_length || 0;
				const isInsufficient =
					availableLength < requiredLength && requiredLength > 0;

				return (
					<span
						style={{
							color: isInsufficient ? 'red' : 'inherit',
							fontWeight: isInsufficient ? 'bold' : 'normal',
						}}
					>
						{`${availableLength} IN.`}
					</span>
				);
			},
			visible: true,
			key: 'total_available_length',
			sorter: true,

			...(filtersEnabled
				? MinimumFilterDropdown(
						{ queryParams: props.queryParams!, navigate: props.navigate! },
						'min_total_available_length',
						'Length',
						props.totalAvailableLengthRange
				  )
				: {}),
		},
		{
			title: 'Material Cert',
			width: '10%',
			render: (record: Material) =>
				!record.has_a_material_certificate && record.certificate_not_available ? (
					<Tooltip title="The vendor does not certify this material, so no mill cert is expected.">
						<Tag>
							<MinusCircleOutlined /> No cert available
						</Tag>
					</Tooltip>
				) : (
					<Tag color={record.has_a_material_certificate ? 'green' : 'red'}>
						{record.has_a_material_certificate ? (
							<>
								<CheckCircleFilled /> Cert
							</>
						) : (
							<>
								<WarningFilled /> No Cert
							</>
						)}
					</Tag>
				),
			visible: true,
			key: 'has_certificate',

			...(filtersEnabled
				? CertificateFilterDropdown({
						queryParams: props.queryParams!,
						navigate: props.navigate!,
				  })
				: {}),
		},
		// {
		// 	title: 'Status',
		// 	render: (record: Material) => (
		// 		<Tag
		// 			color={
		// 				record.status === 'Awaiting Delivery'
		// 					? 'yellow'
		// 					: record.status === 'In Stock'
		// 					? 'green'
		// 					: record.status === 'Quality Hold'
		// 					? 'orange'
		// 					: record.status === 'Scrapped'
		// 					? 'red'
		// 					: 'default'
		// 			}
		// 		>
		// 			{record.status}
		// 		</Tag>
		// 	),
		// 	visible: props.showStatusColum,
		// 	sorter: true,
		// 	key: 'status',
		// },
		{
			title: 'Preview',
			width: '10%',
			key: 'select',
			render: (record: Material) => (
				<>
					{props.onChangePreviewMaterial ? (
						<div>
							{props.selectedPreviewMaterial === record.id ? (
								<Text type="success" className="flex flex-row gap-1">
									<CheckCircleFilled />
									Previewing
								</Text>
							) : (
								<Button
									onClick={() => props.onChangePreviewMaterial?.(record.id)}
								>
									Preview
								</Button>
							)}
						</div>
					) : null}
				</>
			),
			visible: !!props.onChangePreviewMaterial,
		},
		{
			title: 'Actions',
			key: 'select',
			render: (record: Material) => {
				const handleEdit = () => {
					props.setSelectedMaterial?.(record);
					dispatch(showAddMaterialModal());
				};

				const handleReserve = () => {
					setSelectedMaterial(record);
					setReserveMultipleLengths(true);
					setIsMaterialReservationModalOpen(true);
					setForceRefetch(false);
				};

				return (
					<Dropdown.Button
						type="default"
						menu={{
							items: [
								{
									key: 'edit',
									label: 'Edit Material',
									onClick: () => handleEdit(),
								},
							],
						}}
						onClick={handleReserve}
						disabled={record.total_length === 0 || record.total_length === null}
					>
						<Tooltip title="Reserve lengths from available material">
							Reserve
						</Tooltip>
					</Dropdown.Button>
				);
			},
			visible: props.showActionsColumn,
		},
	].filter((col) => col.visible);

	useEffect(() => {
		// This is where we check if only a single material has been passed, and if so
		// set the id to the expanded rows array so that the details are visible on page load
		if (props?.data?.length === 1) {
			setExpandedRowKeys([props.data[0].id]);
		}
	}, [props.data]);

	function handleCloseMaterialReservationModal() {
		setIsMaterialReservationModalOpen(false);
		setSelectedMaterial(null);
		setSelectedMaterialLength(null);
		setSelectedMaterialReservation(null);
	}

	return (
		<div
			data-testid="fastlane-material-inventory-table"
			data-ready={!props.isLoading ? 'true' : 'false'}
		>
			<CustomResponsiveTable
				scroll={{ x: '720px', y: `calc(100vh - 300px)` }}
				columns={tableColumns}
				data={props?.data?.length > 0 ? props.data : []}
				loading={props.isLoading}
				emptyTable={
					props.tableProps.empty ? props.tableProps.empty : emptyTable
				}
				key={0}
				rowKey="id"
				onRow={(record) =>
					({
						'data-testid': 'fastlane-material-row',
						'data-material-id': record.id,
						...(record.original?.id
							? {
									'data-original-material-id': record.original.id,
									'data-remnant-source-material-id': record.original.id,
							  }
							: {}),
					}) as HTMLAttributes<HTMLElement>
				}
				onChange={props.onChange}
				pagination={{
					pageSize: 20,
					current: currentPageFromUrl,
					pageSizeOptions: [],
					total: props.recordsTotal || 0,
					onChange: (page) => {
						props.onChangePage?.(page);
					},
					defaultPageSize: 20,
					showSizeChanger: false,
				}}
				size={props?.tableProps?.size || 'large'}
				style={{
					background: '#FBFBFB',
					border: '1px solid #D9D9D9',
					borderRadius: '2px',
				}}
				expandable={
					props?.tableProps?.expandable
						? {
								expandedRowRender: (material) => (
									<MaterialInventoryDetails
										material_id={material.id}
										showMaterialCertificatesPane={
											props.showMaterialCertificatesPane || false
										}
										refetchData={props.refetchData || (() => undefined)}
										lineItem={props?.lineItem}
										setSelectedMaterial={setSelectedMaterial}
										setSelectedMaterialLength={setSelectedMaterialLength}
										setSelectedMaterialReservation={
											setSelectedMaterialReservation
										}
										setSelectedMaterialDocument={setSelectedMaterialDocument}
										setIsMaterialReservationModalOpen={
											setIsMaterialReservationModalOpen
										}
										selectedMaterialDocument={selectedMaterialDocument}
										setIsReturnRemnantsModalOpen={setIsReturnRemnantsModalOpen}
										showReturnRemnantButton={
											props.showReturnRemnantButton || false
										}
										selectedMaterialLength={selectedMaterialLength}
										forceRefetch={forceRefetch}
									/>
								),
								onExpandedRowsChange: (keys) => setExpandedRowKeys(keys),
								expandedRowKeys: expandedRowKeys,
						  }
						: undefined
				}
			/>

			{isMaterialReservationModalOpen && (
		<MaterialReservationModal
			lineItem={props.lineItem}
			material_cut_length={props.material_cut_length}
			saw_kerf={props?.saw_kerf}
			material_cut_quantity={props?.material_cut_quantity}
			required_trim_cut_length={props?.required_trim_cut_length}
			onClose={handleCloseMaterialReservationModal}
				selectedMaterialReservation={selectedMaterialReservation}
				selectedMaterial={selectedMaterial}
				selectedMaterialLength={selectedMaterialLength}
				isMaterialReservationModalOpen={isMaterialReservationModalOpen}
				reserveMultipleLengths={reserveMultipleLengths}
				lockedBuildingId={props.lockedBuildingId}
				refetchData={() => {
					props.refetchData?.();
					setForceRefetch(true);
				}}
			/>
			)}

			<ReturnRemnantsModal
				isReturnRemnantsModalOpen={isReturnRemnantsModalOpen}
				onClose={() => {
					setIsReturnRemnantsModalOpen(false);
				}}
				refetchData={() => {
					props.refetchData?.();
					getMaterial({ id: selectedMaterial?.id as string });
				}}
				useTabletMode={false}
				materialId={selectedMaterial?.id as string}
			/>

			{contextHolder}
		</div>
	);
};

const emptyTable = () => {
	return (
		<Empty
			image={Empty.PRESENTED_IMAGE_DEFAULT}
			description={
				<span style={{ color: '#000' }}>
					There are currently no materials to be displayed.
				</span>
			}
		/>
	);
};

export default MaterialInventoryTable;
